import { useTranslation } from 'react-i18next';
import Filter, { FilterDropdownOption } from '../../../../core/components/Filter.tsx';
import * as React from 'react';
import { useQuery } from '@apollo/client';
import { useOverviewContext } from './OverviewContext';
import { GET_DOCUMENT_CLIENT_TABLE_FILTER_OPTIONS } from '../../../queries.ts';
import { mapDocumentTypeToGqlDocumentType } from '../../../constants.ts';

export const isValidCustomer = (senderCompany) => {
    // only valid if like "CompanyName (CompanyNumber)"
    if (!senderCompany) {
        return false;
    }
    const trimmed = senderCompany.trim();
    return (
        trimmed.length > 0 &&
        trimmed.includes('(') &&
        trimmed.includes(')') &&
        trimmed.indexOf('(') < trimmed.indexOf(')')
    );
};

export const formatCustomer = (senderCompany) => {
    if (!isValidCustomer(senderCompany)) return null;

    const parts = senderCompany.trim().split('(');
    if (parts?.length > 1) {
        parts[1] = parts[1].replace(/\)\s*$/, '');
    }
    return parts.filter((part) => part.length > 0);
};

const ClientFilter = ({ filters, onFiltersChange }) => {
    const { t } = useTranslation('assistance');

    const [searchValue, setSearchValue] = React.useState('');

    const { documentConfiguration } = useOverviewContext();

    const { data } = useQuery(GET_DOCUMENT_CLIENT_TABLE_FILTER_OPTIONS, {
        fetchPolicy: 'cache-and-network',
        variables: {
            documentType: mapDocumentTypeToGqlDocumentType[documentConfiguration.documentType],
            searchTerm: searchValue,
        },
    });

    const filterName = 'client';
    const filter = filters.find((f) => f.name === filterName);

    const selected = filter ? (Array.isArray(filter.value) ? filter.value : [filter.value]) : [];

    const options = [
        ...selected.map((value) => ({
            value: value,
            label: value,
        })),
        ...(data?.documentClientTableFilterOptions
            ?.filter((option) => !selected.includes(option.value) && isValidCustomer(option.label))
            ?.map((option) => ({
                value: option.value,
                label: option.label,
            })) || []),
    ];

    const handleSelectedChange = (selected: string[]) => {
        const newFilters = filters.filter((f) => f.name !== filterName);
        if (selected.length) {
            newFilters.push({ name: filterName, value: selected, exclude: false, expression: 'eq' });
        }
        onFiltersChange(newFilters);
    };

    const renderOption = (option: FilterDropdownOption) => {
        if (formatCustomer(option.label) == null) {
            return <div className="text-content-weak">({t('overview.columns.notFound')})</div>;
        }
        const [name, number] = formatCustomer(option.label);
        return (
            <div className="flex flex-col space-y-1">
                <div>{name}</div>
                <div className="text-xs text-content-weak">{number || null}</div>
            </div>
        );
    };

    return (
        <Filter
            allLabel={t('overview.filters.client.all')}
            label={t('overview.filters.client.label')}
            options={options}
            selected={selected}
            onSelectedChange={handleSelectedChange}
            renderOption={renderOption}
            multiple
            showSearch
            maxOptions={20}
            searchValue={searchValue}
            onSearchValueChange={setSearchValue}
        />
    );
};

export default ClientFilter;
